import * as React from "react"
import {
  type ApolloQueryResult,
  type FetchMoreOptions,
  type FetchMoreQueryOptions,
  NetworkStatus,
} from "@apollo/client"
import { type Pagination } from "@digits-graphql/frontend/graphql-bearer"
import ScrollListener, {
  ScrollDirection,
} from "@digits-shared/components/UI/Elements/ScrollListener"

export type PaginatorHasMore<Data> = (newData: Data) => boolean

interface PaginatorProps<Data, Variables extends PaginationProps> {
  initialPagination: Pagination
  networkStatus: NetworkStatus
  fetchMore: (
    fetchMoreOptions: FetchMoreQueryOptions<Pick<Variables, "pagination">, Data> &
      FetchMoreOptions<Data, Variables>
  ) => Promise<ApolloQueryResult<Data | undefined>>
  dataField?: string
  hasMore?: PaginatorHasMore<Data>
  stopPagination?: boolean
}

interface PaginationProps {
  pagination?: Pagination
}

export function Paginator<Data, Variables extends PaginationProps>(
  props: PaginatorProps<Data, Variables>
) {
  const loadMore = useLoadMore(props)
  const { dataField, hasMore } = props

  if (!dataField && !hasMore) {
    throw new Error("must specified either a dataField or a hasMore and combinedNewData")
  }

  return <ScrollListener direction={ScrollDirection.Downwards} loadMore={loadMore} />
}

export function useLoadMore<Data, Variables extends PaginationProps>({
  stopPagination,
  initialPagination: { offset: initialOffset, ...pagination },
  networkStatus,
  fetchMore,
  hasMore,
  dataField,
}: PaginatorProps<Data, Variables>) {
  if (!dataField && !hasMore) {
    throw new Error("must specified either a dataField or a hasMore and combinedNewData")
  }

  const [hasMorePages, setHasMorePages] = React.useState(true)
  const [offset, setOffset] = React.useState(initialOffset)
  const loadingRef = React.useRef(false)

  return React.useCallback(() => {
    if (networkStatus !== NetworkStatus.ready || loadingRef.current) return

    if (!hasMorePages || stopPagination) return false

    loadingRef.current = true

    fetchMore({
      variables: {
        pagination: {
          ...pagination,
          offset: offset + pagination.limit,
        },
      },
    }).then((newData: ApolloQueryResult<Data>) => {
      loadingRef.current = false

      if (!newData.data) return
      // If new page callback specified, use it. Otherwise fallback to generic method to determine
      // if there is more pages to be fetched.
      hasMore
        ? setHasMorePages(hasMore(newData.data))
        : setHasMorePages(hasMoreFromField(dataField, pagination.limit, newData.data))
    })

    return setOffset((previousOffset) => previousOffset + pagination.limit)
  }, [
    fetchMore,
    hasMore,
    dataField,
    hasMorePages,
    networkStatus,
    offset,
    pagination,
    stopPagination,
  ])
}

// Meta-programming for determining there is more data left to request. This requires the request
// returns to follow the typical structure of our graphql data. Used if the callback for determining
// if there is another page is not passed.
function hasMoreFromField<Data>(
  dataField: string | undefined,
  paginationLimit: number,
  newData: Data
) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return !!dataField && newData[dataField as keyof Data]?.length === paginationLimit
}
